import { DateTime } from 'luxon';

/**
 * Luxon date helpers for the calendar grids. These replace gripp-OS's
 * `date-fns`/native-`Date` math (`addDays`/`startOfMonth`/`sameDay`/…) in
 * `Calendar.tsx`, kept in one place so the grids stay declarative.
 *
 * The grid is Sunday-first to match gripp-OS (`DOW = ['Sun', … 'Sat']` and
 * `addDays(first, -first.getDay())`). Luxon's `weekday` is Mon=1..Sun=7, so we
 * convert with `weekday % 7` to get Sun=0..Sat=6.
 */

/** Month-cell label headers, Sunday-first (gripp-OS `DOW`). */
export const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

/** Short month names (gripp-OS `MONTHS`). */
export const MONTH_LABELS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

/** Week-grid hour rows: 06:00–18:00 inclusive (13 rows), per §1 / gripp-OS. */
export const WEEK_HOURS: number[] = Array.from({ length: 13 }, (_, i) => i + 6);

/** First / last visible hour rows in the week grid (6am / 6pm). */
export const WEEK_FIRST_HOUR = WEEK_HOURS[0]; // 6
export const WEEK_LAST_HOUR = WEEK_HOURS[WEEK_HOURS.length - 1]; // 18

/**
 * Clamp an hour-of-day into the visible 6am–6pm window (plan §6.8): events before
 * 6am surface in the 6am row, after 6pm in the 6pm row — never dropped.
 */
export const clampHour = (hour: number): number =>
  hour < WEEK_FIRST_HOUR ? WEEK_FIRST_HOUR : hour > WEEK_LAST_HOUR ? WEEK_LAST_HOUR : hour;

/** Sunday-first weekday index (0 = Sun … 6 = Sat) for a luxon DateTime. */
export const sundayIndex = (d: DateTime): number => d.weekday % 7;

/** Start of the calendar grid week containing `d` (the preceding Sunday). */
export const startOfGridWeek = (d: DateTime): DateTime =>
  d.startOf('day').minus({ days: sundayIndex(d) });

/**
 * 6×7 month matrix (42 cells) anchored on `anchor`'s month, including the
 * leading/trailing days that fill the grid — mirrors gripp-OS's
 * `addDays(startOfMonth(anchor), -first.getDay())` + 42-cell loop.
 */
export const monthMatrix = (anchor: DateTime): DateTime[] => {
  const gridStart = startOfGridWeek(anchor.startOf('month'));
  return Array.from({ length: 42 }, (_, i) => gridStart.plus({ days: i }));
};

/** The 7 days of the grid week containing `anchor` (Sun→Sat). */
export const weekDays = (anchor: DateTime): DateTime[] => {
  const start = startOfGridWeek(anchor);
  return Array.from({ length: 7 }, (_, i) => start.plus({ days: i }));
};

/** Same calendar day (gripp-OS `sameDay`). */
export const isSameDay = (a: DateTime, b: DateTime): boolean => a.hasSame(b, 'day');

/** Same calendar month + year (used for out-of-month dimming). */
export const isSameMonth = (a: DateTime, b: DateTime): boolean => a.hasSame(b, 'month');

/** Is `d`'s calendar day after `today`'s — day-granular (gripp-OS `isFutureDay`). */
export const isFutureDay = (d: DateTime, today: DateTime): boolean =>
  d.startOf('day') > today.startOf('day');

/** Month-mode toolbar label, e.g. "Jun 2026" (gripp-OS month `label`). */
export const monthLabel = (anchor: DateTime): string =>
  `${MONTH_LABELS[anchor.month - 1]} ${anchor.year}`;

/**
 * Week-mode toolbar label, e.g. "Jun 21 – Jun 27" (gripp-OS week `label`).
 * Uses an en-dash separator to match gripp-OS.
 */
export const weekLabel = (anchor: DateTime): string => {
  const s = startOfGridWeek(anchor);
  const e = s.plus({ days: 6 });
  return `${MONTH_LABELS[s.month - 1]} ${s.day} – ${MONTH_LABELS[e.month - 1]} ${e.day}`;
};

/** Advance/retreat the anchor by one unit of the current mode (gripp-OS `nav`). */
export const navigateAnchor = (
  anchor: DateTime,
  mode: 'month' | 'week',
  direction: number,
): DateTime =>
  mode === 'month'
    ? anchor.plus({ months: direction })
    : anchor.plus({ weeks: direction });
